import React, { useState, useEffect } from 'react';
import { Line } from 'types';
import { Loader } from 'components';
import Collapse, { Panel } from 'rc-collapse';
import styled from 'styled-components';
import { useGetLinesQuery } from 'api';
import MachineList from './MachineList';
import { BuildingIcon } from 'icons/indexed-icons/BuildingIcon';
import NavHeaderNode from './NavHeaderNode';
import LineCollapse from './LineCollapse';
import { mapBusinessUnit } from 'common/helpers/v1/machine';
import { useHistory } from 'react-router-dom';
import { JBTRoutes } from 'constants/routes';
import { PlantCollapseArgs } from 'components/SidebarNav/NavFlyout/PlantCollapse';
import { FleetNavigationEntityType, useFleetNavigation } from 'providers';

const PlantPanel = styled(Panel)`
  margin-top: 1.625rem;
  margin-left: 1.5rem;
  width: 100%;
  display: inline-block;
`;

const PlantLevelMachineList = styled((props) => <MachineList {...props} />)``;
const PlantCollapseV1 = ({ plants, isLoading }: PlantCollapseArgs): React.ReactElement | null => {
  const history = useHistory();
  const navContext = useFleetNavigation();
  const [activePlantKeys, setActivePlantKeys] = useState<React.Key[]>([]);
  const { data: lines, isFetching: linesLoading } = useGetLinesQuery({
    plantIds: plants?.map((p) => p.id)
  });
  const navFlyoutHighlight = navContext.getFlyoutHighlight();
  const highlightColor = navFlyoutHighlight[FleetNavigationEntityType.PLANT] as string;

  useEffect(() => {
    if (navContext.entityPath.plant) {
      setActivePlantKeys([navContext.entityPath.plant as React.Key]);
    }
  }, [navContext.entityPath.plant]);

  if (isLoading) {
    return <Loader />;
  } else if (plants === undefined) {
    return null;
  }

  return (
    <Collapse
      onChange={(key: React.Key | React.Key[]) =>
        setActivePlantKeys(Array.isArray(key) ? key : [key])
      }
      defaultActiveKey={navContext.entityPath?.plant ?? undefined}
      destroyInactivePanel={true}
    >
      {plants?.map((p) => {
        const plantLines: Line[] | undefined = lines?.filter((l: Line) => l.plantId == p.id);
        const hasLines = plantLines && plantLines.length > 0;
        const plantOpened = activePlantKeys?.includes(p.id);
        const businessUnits = p.machines.map((m) => mapBusinessUnit(m.businessUnit));
        const plantClickable =
          !!navContext.entityLinks?.plant &&
          (businessUnits.includes('pemea') ||
            businessUnits.includes('pna') ||
            businessUnits.includes('dsi') ||
            businessUnits.includes('avure'));
        return (
          <PlantPanel
            showArrow={true}
            collapsible="header"
            key={p.id}
            header={NavHeaderNode(
              p.siteName,
              BuildingIcon,
              plantOpened,
              undefined,
              plantClickable,
              () => {
                if (plantClickable) {
                  if (parseFloat(import.meta.env.VITE_NAV_VERSION as string) >= 2) {
                    history.push(navContext.entityLinks?.plant?.replace(':plantId', p.id) ?? '');
                  } else {
                    history.push(
                      businessUnits.includes('pemea') || businessUnits.includes('pna')
                        ? JBTRoutes.site.replace(':plantId', p.id)
                        : businessUnits.includes('avure')
                        ? `${JBTRoutes.fleet}?plantId=${p.id}`
                        : JBTRoutes.site.replace(':plantId', p.id)
                    );
                  }
                  navContext.setShowFlyout(false);
                  navContext.setMobileSidebarNavOpen(false);
                }
              },
              navContext.entityPath?.plant === p.id,
              highlightColor
            )}
          >
            {hasLines && !linesLoading && <LineCollapse lines={plantLines} machines={p.machines} />}
            {!hasLines && !linesLoading && (
              <PlantLevelMachineList machines={p.machines} nameLength={22} />
            )}
          </PlantPanel>
        );
      })}
    </Collapse>
  );
};

export default PlantCollapseV1;
